/**
 * Custom SSR fetcher for CatalogEntryRecommendation.
 *
 * Overrides the core version to skip the per-product SSR fetch (getProduct with _Details profile).
 * The client component fetches products via useProducts with the Emarsys carousel profile,
 * which uses a different SWR key — so the SSR product data was never reused and only bloated __NEXT_DATA__.
 */

import { getCommerceAI } from '@/data/Content/CommerceAI-Server';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import {
	ESpotContainerType,
	getESpotDataFromName,
	useESpotDataFromName,
} from '@/data/Content/_ESpotDataFromName';
import { getLocalization } from '@/data/Localization-Server';
import { MARKETING_SPOT_DATA_TYPE } from '@/data/constants/marketing';
import { ID } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { WidgetProperties } from '@/data/types/Slot';
import { getPrioritizedPartNumbers } from '@/data/utils/recommendationOrder';
import { useCatalogEntryRecommendation as useCoreCatalogEntryRecommendation } from 'integration/data/core/Content/CatalogEntryRecommendation';
import { useMemo } from 'react';

/**
 * Wraps the core hook so manually configured eSpots respect the priority (displaySequence)
 * set in CMC. Commerce AI results keep the order returned by the model.
 */
export const useCatalogEntryRecommendation = (emsName: ID, properties?: WidgetProperties) => {
	const recommendation = useCoreCatalogEntryRecommendation(emsName, properties);
	// same SWR key as the core hook, so this does not trigger another request
	const { data } = useESpotDataFromName(emsName);
	const prioritizedPartNumbers = useMemo(() => getPrioritizedPartNumbers(data), [data]);

	return {
		...recommendation,
		partNumbers: recommendation.aiModelData?.length
			? recommendation.partNumbers
			: prioritizedPartNumbers,
	};
};

const dataMap = (spots?: ESpotContainerType) => {
	const baseMarketingSpotActivityData =
		spots?.MarketingSpotData?.at(0)?.baseMarketingSpotActivityData || [];
	const aiModelData = baseMarketingSpotActivityData
		.filter(
			({ baseMarketingSpotDataType }) =>
				baseMarketingSpotDataType === MARKETING_SPOT_DATA_TYPE.AI_ML_MODEL
		)
		.map(({ baseMarketingSpotActivityName: modelId }) => ({ modelId }))
		.filter(Boolean);
	return { aiModelData };
};

export const getCatalogEntryRecommendation = async ({
	cache,
	id: _id,
	context,
	properties,
}: ContentProps) => {
	const { localeName: locale } = await getStoreLocale({ cache, context });
	const spot = await getESpotDataFromName(cache, properties?.emsName ?? '', context);
	if (spot) {
		const { aiModelData } = dataMap(spot);
		if (aiModelData?.length) {
			await getCommerceAI({
				cache,
				context,
				properties,
				id: aiModelData.at(0)?.modelId as string,
			});
		}
	}
	await getLocalization(cache, locale, 'productDetail');
};
